import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, Download, FileText, Loader2, Minus, Plus } from 'lucide-react';
import { SkloKruh } from '../../components/ui/Sklo';
import { Z_NAD_LISTOU } from '../../components/ui/ModalShell';
import { ROZOSTRENI_NAVIGACE } from '../../lib/uiTokens';
import { nactiPdfjs } from '../../lib/pdfjs';

/**
 * Report ze směny — PDF na celou obrazovku (norma: standard-reporty.md §4).
 *
 * Samostatná stránka, ne modal: proklik z push notifikace otevírá aplikaci
 * rovnou sem a Zpět vede tam, odkud člověk přišel. Kreslí se přes portál nad
 * vším (i nad mobilní lištou), ovládání plave ve skle u hran a respektuje
 * safe-area — nic se nelepí k výřezu ani ke spodnímu tahu.
 *
 * PDF se kreslí přes pdfjs uvnitř aplikace: otevřené v prohlížeči by v iOS
 * PWA skončilo mimo přihlášení a na Androidu stažením souboru.
 *
 * **Zoom mění rozvržení, ne jen obraz.** Během gesta se obsah jen škáluje
 * transformem (plynulé), po puštění prstů se zoom zapíše do šířky stránek a
 * canvasy se překreslí v odpovídajícím rozlišení. Díky tomu zůstává obyčejný
 * nativní scroll v obou osách — přiblížená tabulka se posouvá prstem stejně
 * jako každá jiná stránka a text je ostrý.
 */

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const DVOJKLIK_ZOOM = 2.5;
/** Kolikrát jemněji se kreslí proti CSS velikosti (ostré na retina). */
const OSTROST = 1.5;
/** Strop šířky canvasu v px — nad ním mobilní prohlížeče alokaci odmítají. */
const MAX_PODKLAD_PX = 4096;
/** Mezera mezi stránkami při zoomu 1 (škáluje se se zoomem). */
const MEZERA = 14;

/**
 * Ovládání prohlížeče je **vždy tmavé sklo s bílými ikonami** — plave nad
 * bílým papírem i nad tmavým pozadím a světlé sklo by se nad stránkou ztratilo.
 * Stejně jako prohlížeč fotek: motiv aplikace se tu neuplatní.
 */
const SKLO = {
    background: 'rgba(28,28,28,0.74)',
    backdropFilter: ROZOSTRENI_NAVIGACE,
    WebkitBackdropFilter: ROZOSTRENI_NAVIGACE,
    boxShadow: '0 6px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.14)',
    color: '#FFFFFF',
};
const POZADI = '#1A1A1A';

const pdfUrl = (id) => `/api/finance/pokladna/report-smeny/${id}/pdf`;
const omez = (z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));

export default function ReportSmenyStranka({ id, onNavigate }) {
    const [hlavicka, setHlavicka] = useState(null);
    const [pdfDoc, setPdfDoc] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [zaklad, setZaklad] = useState(0);

    const plochaRef = useRef(null);
    const obsahRef = useRef(null);
    const zoomRef = useRef(1);
    const cekajiciScroll = useRef(null);

    // ── Data ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!id) { setChyba('Report nebyl nalezen.'); return; }
        let zruseno = false;
        setChyba(null);
        setPdfDoc(null);
        fetch(`/api/finance/pokladna/report-smeny/${id}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (!zruseno) setHlavicka(d); })
            .catch(() => {});
        (async () => {
            try {
                const pdfjs = await nactiPdfjs();
                const doc = await pdfjs.getDocument(pdfUrl(id)).promise;
                if (!zruseno) setPdfDoc(doc);
            } catch (err) {
                if (zruseno) return;
                setChyba(err?.status === 403 ? 'K reportu ze směny nemáš přístup.'
                    : err?.status === 404 ? 'Report nebyl nalezen.'
                    : 'Report se nepodařilo načíst.');
            }
        })();
        return () => { zruseno = true; };
    }, [id]);

    // Stránka pod portálem nesmí scrollovat — na dotyku by se hýbala ona.
    useEffect(() => {
        const predtim = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = predtim; };
    }, []);

    // Šířka stránky při zoomu 1 = šířka plochy bez vnitřních okrajů.
    useLayoutEffect(() => {
        const el = plochaRef.current;
        if (!el) return;
        const zmer = () => {
            const cs = getComputedStyle(el);
            setZaklad(Math.max(0, el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)));
        };
        zmer();
        const ro = new ResizeObserver(zmer);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // ── Zoom kolem bodu ─────────────────────────────────────────────────
    // `mx, my` = bod na obrazovce (vůči ploše), který má zůstat pod prstem.
    const priblizKolem = useCallback((novy, mx, my) => {
        const plocha = plochaRef.current;
        const obsah = obsahRef.current;
        if (!plocha || !obsah) return;
        const stary = zoomRef.current;
        novy = omez(novy);
        if (Math.abs(novy - stary) < 0.001) return;
        const cx = plocha.scrollLeft + mx - obsah.offsetLeft;
        const cy = plocha.scrollTop + my - obsah.offsetTop;
        const s = novy / stary;
        cekajiciScroll.current = { cx: cx * s, cy: cy * s, mx, my };
        zoomRef.current = novy;
        setZoom(novy);
    }, []);

    // Po zápisu nového zoomu do rozvržení srovnat scroll, ať bod zůstane pod prstem.
    useLayoutEffect(() => {
        const c = cekajiciScroll.current;
        const plocha = plochaRef.current;
        const obsah = obsahRef.current;
        if (!c || !plocha || !obsah) return;
        cekajiciScroll.current = null;
        plocha.scrollLeft = obsah.offsetLeft + c.cx - c.mx;
        plocha.scrollTop = obsah.offsetTop + c.cy - c.my;
    }, [zoom]);

    // ── Gesta: dva prsty, dvojité ťuknutí, Ctrl + kolečko ───────────────
    useEffect(() => {
        const plocha = plochaRef.current;
        if (!plocha) return;
        let g = null;
        let posledniTuk = { t: 0, x: 0, y: 0 };

        const bod = (t) => {
            const r = plocha.getBoundingClientRect();
            return { x: t.clientX - r.left, y: t.clientY - r.top };
        };
        const vzdalenost = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

        const start = (e) => {
            if (e.touches.length !== 2) return;
            const obsah = obsahRef.current;
            if (!obsah) return;
            const [a, b] = e.touches;
            const m = bod({ clientX: (a.clientX + b.clientX) / 2, clientY: (a.clientY + b.clientY) / 2 });
            g = {
                d0: vzdalenost(a, b), mx: m.x, my: m.y, s: 1,
                ox: plocha.scrollLeft + m.x - obsah.offsetLeft,
                oy: plocha.scrollTop + m.y - obsah.offsetTop,
            };
            obsah.style.transformOrigin = `${g.ox}px ${g.oy}px`;
            obsah.style.willChange = 'transform';
        };
        const pohyb = (e) => {
            if (!g || e.touches.length !== 2) return;
            e.preventDefault();
            const z0 = zoomRef.current;
            g.s = omez(z0 * vzdalenost(e.touches[0], e.touches[1]) / g.d0) / z0;
            obsahRef.current.style.transform = `scale(${g.s})`;
        };
        const konec = (e) => {
            if (g && e.touches.length < 2) {
                const obsah = obsahRef.current;
                obsah.style.transform = '';
                obsah.style.willChange = '';
                const { s, mx, my } = g;
                g = null;
                priblizKolem(zoomRef.current * s, mx, my);
                return;
            }
            // Dvojité ťuknutí: 1× ↔ 2,5× kolem místa ťuknutí
            if (e.touches.length === 0 && e.changedTouches.length === 1) {
                const p = bod(e.changedTouches[0]);
                const ted = Date.now();
                if (ted - posledniTuk.t < 300 && Math.hypot(p.x - posledniTuk.x, p.y - posledniTuk.y) < 30) {
                    e.preventDefault();
                    priblizKolem(zoomRef.current > 1.05 ? 1 : DVOJKLIK_ZOOM, p.x, p.y);
                    posledniTuk = { t: 0, x: 0, y: 0 };
                } else {
                    posledniTuk = { t: ted, ...p };
                }
            }
        };
        const kolecko = (e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            const p = bod(e);
            priblizKolem(zoomRef.current * Math.exp(-e.deltaY * 0.0025), p.x, p.y);
        };
        // Safari na iOS má vlastní gesto zoomu celé stránky — to tu nechceme.
        const gesto = (e) => e.preventDefault();

        plocha.addEventListener('touchstart', start, { passive: true });
        plocha.addEventListener('touchmove', pohyb, { passive: false });
        plocha.addEventListener('touchend', konec, { passive: false });
        plocha.addEventListener('touchcancel', konec, { passive: false });
        plocha.addEventListener('wheel', kolecko, { passive: false });
        plocha.addEventListener('gesturestart', gesto);
        return () => {
            plocha.removeEventListener('touchstart', start);
            plocha.removeEventListener('touchmove', pohyb);
            plocha.removeEventListener('touchend', konec);
            plocha.removeEventListener('touchcancel', konec);
            plocha.removeEventListener('wheel', kolecko);
            plocha.removeEventListener('gesturestart', gesto);
        };
    }, [priblizKolem]);

    const tlacitkemZoom = (krat) => {
        const plocha = plochaRef.current;
        if (!plocha) return;
        priblizKolem(zoomRef.current * krat, plocha.clientWidth / 2, plocha.clientHeight / 2);
    };

    const zpet = () => (window.history.length > 1 ? window.history.back() : onNavigate?.('prehled'));

    // ── Vzhled ──────────────────────────────────────────────────────────
    const datum = hlavicka?.datum
        ? new Date(hlavicka.datum + 'T12:00:00').toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' })
        : null;
    const sirka = zaklad * zoom;
    const OKRAJ = 16;

    return createPortal((
        <div role="dialog" aria-modal="true" aria-label="Report ze směny"
            style={{ position: 'fixed', inset: 0, zIndex: Z_NAD_LISTOU, background: POZADI }}>

            <div ref={plochaRef}
                style={{
                    position: 'absolute', inset: 0, overflow: 'auto',
                    WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain',
                    touchAction: 'pan-x pan-y',
                    paddingTop: `calc(env(safe-area-inset-top, 0px) + 72px)`,
                    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 96px)`,
                    paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${OKRAJ}px)`,
                    paddingRight: `calc(env(safe-area-inset-right, 0px) + ${OKRAJ}px)`,
                }}>
                {chyba ? (
                    <Hlaska><FileText size={28} />{chyba}</Hlaska>
                ) : !pdfDoc || !zaklad ? (
                    <Hlaska><Loader2 size={20} className="animate-spin" />Načítám report…</Hlaska>
                ) : (
                    <div ref={obsahRef} style={{ width: sirka, margin: zoom === 1 ? '0 auto' : 0 }}>
                        {Array.from({ length: pdfDoc.numPages }, (_, i) => (
                            <Strana key={i} doc={pdfDoc} cislo={i + 1} sirka={sirka} mezera={MEZERA * zoom} />
                        ))}
                        {/* Pravý okraj při přiblížení — padding scroll kontejneru na konci řádku prohlížeče nepočítají. */}
                        <div style={{ position: 'absolute', top: 0, width: 1, height: 1, left: sirka + OKRAJ * 2 - 1 }} />
                    </div>
                )}
            </div>

            {/* Horní ovládání: zpět · název · stáhnout */}
            <div style={{
                position: 'absolute', left: 0, right: 0, top: 0, pointerEvents: 'none',
                paddingTop: `calc(env(safe-area-inset-top, 0px) + 12px)`,
                paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${OKRAJ}px)`,
                paddingRight: `calc(env(safe-area-inset-right, 0px) + ${OKRAJ}px)`,
                display: 'flex', alignItems: 'center', gap: 10,
            }}>
                <SkloKruh Icon={ChevronLeft} ikona={22} popis="Zpět" onClick={zpet} style={{ ...SKLO, pointerEvents: 'auto' }} />
                <div style={{
                    ...SKLO, pointerEvents: 'auto', minWidth: 0, flex: '0 1 auto', margin: '0 auto',
                    height: 44, borderRadius: 999, padding: '0 16px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', lineHeight: 1.15,
                }}>
                    <div className="truncate" style={{ fontSize: 13, fontWeight: 700, color: '#FFFFFF' }}>Report ze směny</div>
                    {datum && (
                        <div className="truncate" style={{ fontSize: 11, color: 'rgba(255,255,255,0.68)' }}>
                            {datum}{hlavicka?.manazer ? ` · ${hlavicka.manazer}` : ''}
                        </div>
                    )}
                </div>
                <SkloKruh Icon={Download} popis="Stáhnout PDF" href={id ? `${pdfUrl(id)}?stahnout=1` : undefined}
                    style={{ ...SKLO, pointerEvents: 'auto' }} />
            </div>

            {/* Spodní ovládání: zoom */}
            {pdfDoc && (
                <div style={{
                    position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                    bottom: `calc(env(safe-area-inset-bottom, 0px) + 20px)`,
                    ...SKLO, height: 44, borderRadius: 999, padding: '0 4px',
                    display: 'flex', alignItems: 'center',
                }}>
                    <ZoomTlacitko Icon={Minus} popis="Oddálit" disabled={zoom <= MIN_ZOOM + 0.001} onClick={() => tlacitkemZoom(1 / 1.5)} />
                    <button type="button" onClick={() => tlacitkemZoom(1 / zoomRef.current)} title="Na šířku obrazovky"
                        style={{
                            minWidth: 56, height: 44, border: 'none', background: 'transparent', cursor: 'pointer',
                            fontSize: 13, fontWeight: 700, color: '#FFFFFF', fontVariantNumeric: 'tabular-nums',
                        }}>
                        {Math.round(zoom * 100)} %
                    </button>
                    <ZoomTlacitko Icon={Plus} popis="Přiblížit" disabled={zoom >= MAX_ZOOM - 0.001} onClick={() => tlacitkemZoom(1.5)} />
                </div>
            )}
        </div>
    ), document.body);
}

function ZoomTlacitko({ Icon, popis, onClick, disabled }) {
    return (
        <button type="button" onClick={onClick} disabled={disabled} aria-label={popis} title={popis}
            style={{
                width: 42, height: 44, border: 'none', background: 'transparent', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: '#FFFFFF', opacity: disabled ? 0.35 : 1, WebkitTapHighlightColor: 'transparent',
            }}>
            <Icon size={18} />
        </button>
    );
}

function Hlaska({ children }) {
    return (
        <div className="flex flex-col items-center justify-center gap-2 text-sm"
            style={{ color: 'rgba(255,255,255,0.6)', paddingTop: '30vh' }}>
            {children}
        </div>
    );
}

/**
 * Jedna stránka PDF. Kreslí se do skrytého canvasu a teprve hotová se
 * přenese — překreslení po zoomu tak neprobliká bílou.
 */
function Strana({ doc, cislo, sirka, mezera }) {
    const canvasRef = useRef(null);
    const [vyska, setVyska] = useState(sirka * 1.414);

    useEffect(() => {
        if (!sirka) return;
        let zruseno = false;
        let task = null;
        const t = setTimeout(async () => {
            try {
                const page = await doc.getPage(cislo);
                if (zruseno) return;
                const zakladVp = page.getViewport({ scale: 1 });
                const fit = sirka / zakladVp.width;
                setVyska(zakladVp.height * fit);
                const dpr = window.devicePixelRatio || 1;
                const chtene = sirka * dpr * OSTROST;
                const scale = fit * dpr * OSTROST * Math.min(1, MAX_PODKLAD_PX / chtene);
                const viewport = page.getViewport({ scale });
                const pomocny = document.createElement('canvas');
                pomocny.width = Math.round(viewport.width);
                pomocny.height = Math.round(viewport.height);
                task = page.render({ canvasContext: pomocny.getContext('2d'), viewport });
                await task.promise;
                const canvas = canvasRef.current;
                if (zruseno || !canvas) return;
                canvas.width = pomocny.width;
                canvas.height = pomocny.height;
                canvas.getContext('2d').drawImage(pomocny, 0, 0);
            } catch (err) {
                if (err?.name !== 'RenderingCancelledException') console.error(err);
            }
        }, 60);
        return () => {
            zruseno = true;
            clearTimeout(t);
            try { task?.cancel(); } catch { /* noop */ }
        };
    }, [doc, cislo, sirka]);

    return (
        <canvas ref={canvasRef}
            style={{
                display: 'block', width: sirka, height: vyska, marginBottom: mezera,
                background: '#fff', borderRadius: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.22)',
            }} />
    );
}
